// Whole-day arithmetic on a `YYYY-MM-DD` logical date (ADR 024 — Time Model).
// BUG-1832 lifted this out of three seed-route copies so the payment-date window
// and the house-day tests share one implementation.
import { parseLogicalDate } from './parse-logical-date';
import { toLogicalDateString } from './to-logical-date-string';

/**
 * Shift a logical date by whole calendar days.
 *
 * Built from UTC parts and read back through the same UTC lens, so the offsets
 * cancel and no zone is ever applied to a value that has none — the sanctioned
 * pure-calendar-arithmetic shape from `logical-dates.md`. Never `.toISOString()`.
 *
 * @param logicalDate - a `YYYY-MM-DD` logical date.
 * @param days - whole days to add (may be negative).
 * @returns the shifted date, still `YYYY-MM-DD`.
 * @throws when `logicalDate` is not a real calendar day — a caller handing in
 *   garbage is a programming error, not a value to coerce.
 */
export function addLogicalDays(logicalDate: string, days: number): string {
    const parts = parseLogicalDate(logicalDate);
    if (!parts) throw new Error(`addLogicalDays: malformed logical date "${logicalDate}"`);
    const shifted = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days));
    return toLogicalDateString({
        year: shifted.getUTCFullYear(),
        month: shifted.getUTCMonth() + 1,
        day: shifted.getUTCDate(),
    });
}
